import { useCallback, useEffect } from 'react';

import { useAuth } from '@opendoor/auth-fe/context';
import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query';
import { redactGraphqlRequestErrorForTelemetry } from 'components/api/graphqlRequestError';
import { requestZeusFromBrowser } from 'components/api/zeusBrowserClient';
import { captureError } from 'helpers/captureError';

import {
  MarketplaceCreateSavedSearchDocument,
  MarketplaceDeleteSavedSearchDocument,
  MarketplaceSavedSearchesDocument,
  MarketplaceUpdateSavedSearchDocument,
} from './__generated__/saved-searches.zeus';
import type {
  SavedSearchFieldsFragment,
  SavedSearchInput,
} from './__generated__/saved-searches.zeus';

type SavedSearchMutationResult = {
  success: boolean;
  errorMessage?: string | null;
  savedSearch?: SavedSearchFieldsFragment | null;
};

export type RemoveSavedSearchResult = {
  success: boolean;
  errorMessage?: string | null;
};

type SavedSearchesFetchResult = {
  savedSearches: SavedSearchFieldsFragment[];
  hasLoaded: boolean;
};

interface UseSavedSearchesApi {
  isLoggedIn: boolean;
  hasLoaded: boolean;
  savedSearches: SavedSearchFieldsFragment[];
  add: (input: SavedSearchInput) => Promise<SavedSearchMutationResult>;
  update: (id: string, input: SavedSearchInput) => Promise<SavedSearchMutationResult>;
  remove: (id: string) => Promise<RemoveSavedSearchResult>;
  refetch: () => Promise<SavedSearchesFetchResult>;
}

const SAVED_SEARCHES_QUERY_ROOT = ['marketplace', 'saved-searches'] as const;
const EMPTY_SAVED_SEARCHES: SavedSearchFieldsFragment[] = [];
const savedSearchesQueryKey = (customerUuid: string | null) =>
  [...SAVED_SEARCHES_QUERY_ROOT, customerUuid] as const;
const savedSearchesQueryOptions = (customerUuid: string | null) =>
  queryOptions({
    queryKey: savedSearchesQueryKey(customerUuid),
    queryFn: async ({ signal }) => {
      const result = await requestZeusFromBrowser(MarketplaceSavedSearchesDocument, {}, signal);
      return result.customerAccount.buyer.savedSearches;
    },
    meta: { captureErrorContext: 'MarketplaceSavedSearches' },
    refetchOnMount: 'always',
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    retry: false,
    staleTime: 0,
  });

const inputHasLocation = (input: SavedSearchInput): boolean =>
  Boolean(input.location.market) ||
  (input.location.zoneIds?.length ?? 0) > 0 ||
  (input.location.vertices?.length ?? 0) > 0;

const LOCATION_REQUIRED_ERROR = 'A location is required to save a search.';
const UNEXPECTED_ERROR = 'Unexpected error';

export function useSavedSearches(): UseSavedSearchesApi {
  const { authentication } = useAuth();
  const queryClient = useQueryClient();
  const authState = authentication.state;
  const isLoggedIn = authState === 'authenticated';
  const customerUuid = isLoggedIn ? authentication.user.customerUuid : null;
  const { data } = useQuery({
    ...savedSearchesQueryOptions(customerUuid),
    enabled: customerUuid !== null,
  });

  useEffect(() => {
    if (authState !== 'authenticated' && authState !== 'authenticating') {
      queryClient.removeQueries({ queryKey: SAVED_SEARCHES_QUERY_ROOT });
    }
  }, [authState, queryClient]);

  const reconcile = useCallback(
    async (
      expectedCustomerUuid: string,
      updateCached: (current: SavedSearchFieldsFragment[]) => SavedSearchFieldsFragment[],
    ) => {
      const expectedQueryKey = savedSearchesQueryKey(expectedCustomerUuid);
      await queryClient.cancelQueries({ queryKey: expectedQueryKey, exact: true });
      queryClient.setQueryData<SavedSearchFieldsFragment[]>(expectedQueryKey, (current) =>
        current ? updateCached(current) : current,
      );
      await queryClient.invalidateQueries({ queryKey: expectedQueryKey, exact: true });
    },
    [queryClient],
  );

  const add = useCallback(
    async (input: SavedSearchInput): Promise<SavedSearchMutationResult> => {
      if (!inputHasLocation(input)) {
        return { success: false, errorMessage: LOCATION_REQUIRED_ERROR, savedSearch: null };
      }
      if (customerUuid === null) {
        return { success: false, errorMessage: UNEXPECTED_ERROR, savedSearch: null };
      }
      try {
        const { buyerSavedSearchCreate } = await requestZeusFromBrowser(
          MarketplaceCreateSavedSearchDocument,
          { input: { savedSearch: input } },
        );
        if (buyerSavedSearchCreate.__typename !== 'SavedSearch') {
          return {
            success: false,
            errorMessage: buyerSavedSearchCreate.message,
            savedSearch: null,
          };
        }
        await reconcile(customerUuid, (current) => {
          const existingIndex = current.findIndex((item) => item.id === buyerSavedSearchCreate.id);
          if (existingIndex === -1) {
            return [...current, buyerSavedSearchCreate];
          }
          return current.map((item, index) =>
            index === existingIndex ? buyerSavedSearchCreate : item,
          );
        });
        return { success: true, errorMessage: null, savedSearch: buyerSavedSearchCreate };
      } catch (mutationError) {
        captureError(
          redactGraphqlRequestErrorForTelemetry(mutationError),
          'MarketplaceCreateSavedSearch',
        );
        return { success: false, errorMessage: UNEXPECTED_ERROR, savedSearch: null };
      }
    },
    [customerUuid, reconcile],
  );

  const update = useCallback(
    async (id: string, input: SavedSearchInput): Promise<SavedSearchMutationResult> => {
      if (!inputHasLocation(input)) {
        return { success: false, errorMessage: LOCATION_REQUIRED_ERROR, savedSearch: null };
      }
      if (customerUuid === null) {
        return { success: false, errorMessage: UNEXPECTED_ERROR, savedSearch: null };
      }
      try {
        const { buyerSavedSearchUpdate } = await requestZeusFromBrowser(
          MarketplaceUpdateSavedSearchDocument,
          { input: { id, savedSearch: input } },
        );
        if (buyerSavedSearchUpdate.__typename !== 'SavedSearch') {
          return {
            success: false,
            errorMessage: buyerSavedSearchUpdate.message,
            savedSearch: null,
          };
        }
        await reconcile(customerUuid, (current) =>
          current.map((item) => (item.id === id ? buyerSavedSearchUpdate : item)),
        );
        return { success: true, errorMessage: null, savedSearch: buyerSavedSearchUpdate };
      } catch (mutationError) {
        captureError(
          redactGraphqlRequestErrorForTelemetry(mutationError),
          'MarketplaceUpdateSavedSearch',
        );
        return { success: false, errorMessage: UNEXPECTED_ERROR, savedSearch: null };
      }
    },
    [customerUuid, reconcile],
  );

  const remove = useCallback(
    async (id: string): Promise<RemoveSavedSearchResult> => {
      if (customerUuid === null) {
        return { success: false, errorMessage: UNEXPECTED_ERROR };
      }
      try {
        const { buyerSavedSearchDelete } = await requestZeusFromBrowser(
          MarketplaceDeleteSavedSearchDocument,
          { input: { id } },
        );
        if (buyerSavedSearchDelete.__typename !== 'CommandResult') {
          return { success: false, errorMessage: buyerSavedSearchDelete.message };
        }
        if (!buyerSavedSearchDelete.success) {
          return { success: false, errorMessage: null };
        }
        await reconcile(customerUuid, (current) => current.filter((item) => item.id !== id));
        return { success: true, errorMessage: null };
      } catch (mutationError) {
        captureError(
          redactGraphqlRequestErrorForTelemetry(mutationError),
          'MarketplaceDeleteSavedSearch',
        );
        return { success: false, errorMessage: UNEXPECTED_ERROR };
      }
    },
    [customerUuid, reconcile],
  );

  const refetch = useCallback(async (): Promise<SavedSearchesFetchResult> => {
    if (customerUuid === null) {
      return { savedSearches: EMPTY_SAVED_SEARCHES, hasLoaded: false };
    }
    try {
      await queryClient.fetchQuery(savedSearchesQueryOptions(customerUuid));
    } catch {
      // QueryCache owns reporting; callers receive the last known successful data.
    }
    const savedSearches = queryClient.getQueryData<SavedSearchFieldsFragment[]>(
      savedSearchesQueryKey(customerUuid),
    );
    return {
      savedSearches: savedSearches ?? EMPTY_SAVED_SEARCHES,
      hasLoaded: savedSearches !== undefined,
    };
  }, [customerUuid, queryClient]);

  return {
    isLoggedIn,
    hasLoaded: data !== undefined,
    savedSearches: data ?? EMPTY_SAVED_SEARCHES,
    add,
    update,
    remove,
    refetch,
  };
}
